import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSpring, animated } from "react-spring";
import { useInView } from "react-intersection-observer";
import { useMediaQuery } from "@mantine/hooks";

import style from "./features.module.css";
import { Flex, Stack } from "@mantine/core";
import InfoSlab from "../../../../molecules/infoSlab/infoSlab";
import FeedbackIllustration from "./illustrations/feedback/feedback-illustration";
import FollowUpIllustration from "./illustrations/follow-up/follow-up-illustration";

const FeaturesSegment = () => {
    const { t } = useTranslation(["landing-page"]);
    const [activeSection, setActiveSection] = useState(0);

    const isMobile = useMediaQuery("(max-width: 850px)");

    const sections = [
        {
            id: 0,
            title: t("features.sections.0.title"),
            content: t("features.sections.0.content"),
            Component: EnglishOriginalComponent,
        },
        {
            id: 1,
            title: t("features.sections.1.title"),
            content: t("features.sections.1.content"),
            Component: FollowUpIllustration,
        },
        {
            id: 2,
            title: t("features.sections.2.title"),
            content: t("features.sections.2.content"),
            Component: FeedbackIllustration,
        },
    ];

    if (isMobile)
        return (
            <div className={style.wrapper}>
                <div className={style.innerWrapper}>
                    {sections.map((section, index) => (
                        <div className={style.centeredContent} key={`feature-${index}`}>
                            <h2 className={style.header}>{section.title}</h2>
                            <p>{section.content}</p>
                            <section.Component />
                        </div>
                    ))}
                </div>
            </div>
        );

    return (
        <div className={style.wrapper}>
            <div className={style.landingContainer}>
                <div className={style.leftContent}>
                    {sections.map((section, index) => (
                        <Section key={section.id} section={section} index={index} setActiveSection={setActiveSection} />
                    ))}
                </div>

                <div className={style.rightContent}>
                    <StickyComponents sections={sections} activeSection={activeSection} />
                </div>
            </div>
        </div>
    );
};

export default FeaturesSegment;

const Section = ({ section, index, setActiveSection }) => {
    const [ref, inView] = useInView({
        threshold: 0,
        delay: 200,
        rootMargin: "-50% 0% -50% 0%",
        triggerOnce: false,
    });

    useEffect(() => {
        if (inView) {
            setActiveSection(index);
        }
    }, [inView, index, setActiveSection]);

    return (
        <div ref={ref} className={style.section}>
            <h2 className={style.header}>{section.title}</h2>
            <p>{section.content}</p>
        </div>
    );
};

const StickyComponents = ({ sections, activeSection }) => {
    return (
        <div className={style.stickyContainer}>
            {sections.map((section, index) => (
                <ComponentRenderer key={section.id} section={section} isActive={activeSection === index} />
            ))}
        </div>
    );
};

const ComponentRenderer = ({ section, isActive }) => {
    const { Component } = section;

    const springProps = useSpring({
        opacity: isActive ? 1 : 0,
        config: { tension: 280, friction: 40 },
    });

    return (
        <animated.div
            className={style.componentWrapper}
            style={{
                ...springProps,
                pointerEvents: isActive ? "auto" : "none",
                position: "absolute",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Component />
        </animated.div>
    );
};

const EnglishOriginalComponent = () => {
    const { t } = useTranslation(["landing-page"]);

    return (
        <div className={style.screenshotWrapper}>
            <Flex columnGap={"1rem"}>
                <Stack spacing={"0.5rem"}>
                    <p className={style.screenshotText}>{t("features.example.paragraph1")}</p>

                    <p className={style.screenshotText}>{t("features.example.paragraph2")}</p>
                </Stack>

                <Stack miw={170}>
                    <InfoSlab aiScore={0.3} displayExplainations={true} scan={{ version: "2.1" }} smallVersion={true} />
                </Stack>
            </Flex>
        </div>
    );
};
